import React from 'react'
import { Card, CardContent } from './ui/card'
import { Sparkles, ArrowRight, User, Building2, Download, LockOpen } from 'lucide-react'
import { PaymentMeta } from '@/constants/PaymentMeta'
import { Button } from './ui/button'
import { Tip } from './custom-tooltip'

export type PricingProps = Record<string, never>
export const Pricing = ({}: PricingProps) => {
  return (
    <>
      <section
        id="pricing"
        className="mb-16 text-center scroll-mt-24"
        aria-labelledby="pricing-heading"
      >
        <h2
          id="pricing-heading"
          className="text-3xl font-semibold mb-12 text-center text-foreground"
        >
          Pricing
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <Card className="relative group hover:border-primary transition-all duration-300 hover:shadow-lg">
            <CardContent className="pt-6 h-full flex flex-col">
              <h4 className="text-lg font-semibold mb-4 text-foreground">Personal</h4>
              <div className="mb-1">
                <p className="text-4xl font-bold text-foreground">
                  ${PaymentMeta.personal.cost}
                  <span className="text-lg text-muted-foreground">/year</span>
                </p>
              </div>
              <p className="text-sm text-muted-foreground mb-4">local taxes may apply</p>
              <ul className="text-left mb-6 space-y-3 grow">
                <li className="text-muted-foreground flex">
                  <Sparkles className="w-4 h-4 mr-2 inline shrink-0 mt-1" />
                  <span>
                    <a
                      href="https://react-native-google-signin.github.io/docs/one-tap"
                      className="text-primary underline hover:text-primary/80 transition-colors"
                      target={'_blank'}
                    >
                      Universal Google Sign-In
                    </a>
                    , advanced security features, easier configuration and more
                  </span>
                </li>
                <li className="text-muted-foreground flex">
                  <User className="w-4 h-4 mr-2 inline shrink-0 mt-1" />
                  <span>For solo developers and freelancers working on their next idea</span>
                </li>
                <li className="text-muted-foreground flex">
                  <Download className="w-4 h-4 mr-2 inline shrink-0 mt-1" />
                  <span>Receive updates while subscribed, cancel anytime</span>
                </li>
                <li className="text-muted-foreground flex">
                  <LockOpen className="w-4 h-4 mr-2 inline shrink-0 mt-1" />
                  <span>
                    Use the package even after subscription ends{' '}
                    <span className="whitespace-nowrap">
                      (
                      <Tip content="Access to the private npm registry stops working at the end of the subscription. Before subscription expires, you can store the package in your project for future installs.">
                        <span className="inline underline decoration-dotted">self-hosted</span>
                      </Tip>
                      )
                    </span>
                  </span>
                </li>
              </ul>
              <Button
                size="lg"
                className="w-full mt-auto bg-foreground text-background hover:bg-foreground/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
                asChild
              >
                <a
                  href={PaymentMeta.personal.link}
                  target="_blank"
                  className="flex items-center justify-center"
                >
                  Subscribe
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </CardContent>
          </Card>

          <Card className="relative group hover:border-primary transition-all duration-300 hover:shadow-lg">
            <CardContent className="pt-6 h-full flex flex-col">
              <h4 className="text-lg font-semibold mb-4 text-foreground">Team</h4>
              <div className="mb-1">
                <p className="text-4xl font-bold text-foreground">
                  ${PaymentMeta.team.cost}
                  <span className="text-lg text-muted-foreground">/year</span>
                </p>
              </div>
              <p className="text-sm text-muted-foreground mb-4">local taxes may apply</p>
              <ul className="text-left mb-6 space-y-3 grow">
                <li className="text-muted-foreground flex">
                  <Building2 className="w-4 h-4 mr-2 inline shrink-0 mt-1" />
                  <span>
                    For companies, teams, and agencies - up to {PaymentMeta.team.seats} developers
                    per license
                  </span>
                </li>
                <li className="text-muted-foreground flex">
                  <Sparkles className="w-4 h-4 mr-2 inline shrink-0 mt-1" />
                  <span>All features included</span>
                </li>
                <li className="text-sm text-muted-foreground italic">
                  Have more than {PaymentMeta.team.seats} developers?{' '}
                  <a
                    href="mailto:hello@universal-sign-in.com"
                    className="text-primary underline hover:text-primary/80 transition-colors not-italic"
                  >
                    Contact us
                  </a>{' '}
                  for custom pricing.
                </li>
              </ul>
              <Button
                size="lg"
                className="w-full mt-auto bg-foreground text-background hover:bg-foreground/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
                asChild
              >
                <a
                  href={PaymentMeta.team.link}
                  target="_blank"
                  className="flex items-center justify-center"
                >
                  Subscribe
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>

        <p className="text-sm text-muted-foreground text-center mt-6">
          Are you an EAS customer?{' '}
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSeG4JV0yUUkuRg_stlKMewKdvrvoFxxBUPDZf0UrS2VrYCd4Q/viewform?usp=send_form"
            className="text-primary underline hover:text-primary/80 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            You may be eligible for a free license.
          </a>
        </p>
      </section>
    </>
  )
}
